/**
 * Auth helpers — login, logout, and profile fetch.
 *
 * Token state lives in authStore, which persists it to localStorage
 * (`stores/authStore.ts`) — shared by every tab in the browser, surviving a
 * refresh, a new tab, and a closed tab alike. That reach is bounded: after
 * VITE_AUTH_IDLE_HOURS with no input the session ends in every tab
 * (`lib/idleExpiry.ts`), and logout() below ends it everywhere at once.
 * It was per-tab sessionStorage until AR-2c, and in-memory before the store
 * gained `persist`.
 */
import { useAuthStore } from '@/stores/authStore'
import { startActivityClock } from '@/lib/idleExpiry'
import type { User } from '@/types'

export async function login(email: string, password: string): Promise<void> {
  const resp = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    throw new Error(body.detail ?? 'Invalid email or password.')
  }

  const data = await resp.json()

  // Fetch user profile using the new token
  const meResp = await fetch('/api/auth/me', {
    headers: { Authorization: `Bearer ${data.access_token}` },
  })

  if (!meResp.ok) {
    const body = await meResp.json().catch(() => ({}))
    throw new Error(body.detail ?? 'Login succeeded but profile fetch failed.')
  }

  const profile: User = await meResp.json()
  // The clock first, then the session: a tab that saw the new session beside
  // the previous one's stale clock would expire it on arrival.
  startActivityClock()
  useAuthStore.getState().setAuth(profile, data.access_token, data.refresh_token)
}

export function logout(): void {
  useAuthStore.getState().clearAuth()
  // Clearing memory is not the whole job once a session is shared: the
  // persisted copy would outlive it, and the next tab to boot would
  // hydrate a session its owner had just ended. Removing the key is also
  // the signal every sibling tab acts on — see lib/authSync.ts. It runs
  // second on purpose: clearAuth() writes through the persist middleware,
  // so clearing first would leave the key behind, rewritten and empty.
  useAuthStore.persist.clearStorage()
}

export function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}
